(() => {
  const DESKTOP = window.matchMedia('(min-width: 1000px)');
  const GALLERY_LIMIT = 4;
  const galleryObservers = new WeakMap();
  let sectionObserver = null;
  let queued = false;

  const svg = (paths) => `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
      ${paths}
    </svg>`;

  const benefitsMarkup = () => `
    <p class="ph-pdp-benefits__title">Why buy from Panel Hub</p>
    <div class="ph-pdp-benefits__grid">
      <div class="ph-pdp-benefit">
        <span class="ph-pdp-benefit__icon">${svg('<path d="M3 6h11v10H3z"/><path d="M14 10h3l4 4v2h-7z"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>')}</span>
        <span><strong>Delivery options at checkout</strong><span>Costs and estimated timescales are shown before you pay.</span></span>
      </div>
      <div class="ph-pdp-benefit">
        <span class="ph-pdp-benefit__icon">${svg('<path d="M4 7v5h5"/><path d="M5 12a7 7 0 1 0 2-5"/><path d="M4 7l3-3"/>')}</span>
        <span><strong>Straightforward returns</strong><span>Clear returns policy; your statutory rights are unaffected.</span></span>
      </div>
      <div class="ph-pdp-benefit">
        <span class="ph-pdp-benefit__icon">${svg('<rect x="5" y="10" width="14" height="10" rx="1"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>')}</span>
        <span><strong>Secure checkout</strong><span>Shop confidently using trusted payment methods.</span></span>
      </div>
      <div class="ph-pdp-benefit">
        <span class="ph-pdp-benefit__icon">${svg('<path d="M4 13v-2a8 8 0 0 1 16 0v2"/><path d="M4 13h3v6H5a1 1 0 0 1-1-1z"/><path d="M20 13h-3v6h2a1 1 0 0 0 1-1z"/><path d="M17 19c0 1.1-1.8 2-4 2"/>')}</span>
        <span><strong>Expert help</strong><span>Speak to our team on <a href="tel:+448001577574">0800 157 7574</a>.</span></span>
      </div>
    </div>`;

  const nativeVisibleMedia = (gallery) => {
    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel) return [];
    return Array.from(carousel.querySelectorAll('.product-gallery__media')).filter((item) => !item.hidden);
  };

  const getKeptMedia = (media) => {
    if (media.length <= GALLERY_LIMIT) return media;

    const initial = media.find((item) => item.classList.contains('is-initial'));
    if (!initial || media.indexOf(initial) < GALLERY_LIMIT) return media.slice(0, GALLERY_LIMIT);

    return [...media.slice(0, GALLERY_LIMIT - 1), initial];
  };

  const recalcGallery = (gallery) => {
    if (!gallery.isConnected) return;

    const media = nativeVisibleMedia(gallery);
    const kept = new Set(getKeptMedia(media));
    const expanded = gallery.dataset.phPdpExpanded === 'true';
    const desktop = DESKTOP.matches;

    gallery.querySelectorAll('.product-gallery__media').forEach((item) => {
      item.classList.toggle('ph-pdp-media-extra', !item.hidden && !kept.has(item));
    });

    const shouldCollapse = desktop && media.length > GALLERY_LIMIT && !expanded;
    gallery.classList.toggle('ph-pdp-gallery-collapsed', shouldCollapse);

    const button = gallery.querySelector('.ph-pdp-view-all');
    if (!button) return;

    button.hidden = !desktop || media.length <= GALLERY_LIMIT;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    button.setAttribute('aria-label', expanded ? 'Show fewer product images' : `View all ${media.length} product images`);

    const nextText = expanded ? 'SHOW LESS' : `VIEW ALL (${media.length})`;
    if (button.textContent !== nextText) button.textContent = nextText;
  };

  const initialiseGallery = (gallery) => {
    if (gallery.dataset.phPdpVersionBReady === 'true') {
      recalcGallery(gallery);
      return;
    }

    gallery.dataset.phPdpVersionBReady = 'true';
    gallery.dataset.phPdpExpanded = 'false';

    const imageList = gallery.querySelector('.product-gallery__image-list');
    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (!imageList || !carousel) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ph-pdp-view-all';
    button.hidden = true;
    button.addEventListener('click', () => {
      gallery.dataset.phPdpExpanded = gallery.dataset.phPdpExpanded === 'true' ? 'false' : 'true';
      recalcGallery(gallery);

      if (gallery.dataset.phPdpExpanded !== 'true') {
        gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    imageList.insertAdjacentElement('afterend', button);

    let galleryQueued = false;
    const observer = new MutationObserver(() => {
      if (galleryQueued) return;
      galleryQueued = true;
      requestAnimationFrame(() => {
        galleryQueued = false;
        recalcGallery(gallery);
      });
    });

    observer.observe(carousel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden']
    });
    galleryObservers.set(gallery, observer);

    recalcGallery(gallery);
  };

  const installBenefits = (section) => {
    const blockList = section.querySelector('.product-info__block-list');
    if (!blockList || blockList.querySelector('[data-ph-pdp-version-b-benefits]')) return;

    const buyBlock = blockList.querySelector('.product-info__block-item[data-block-type="buy-buttons"]');
    if (!buyBlock) return;

    const panel = document.createElement('div');
    panel.className = 'ph-pdp-benefits';
    panel.dataset.phPdpVersionBBenefits = 'true';
    panel.setAttribute('aria-label', 'Why buy from Panel Hub');
    panel.innerHTML = benefitsMarkup();
    buyBlock.insertAdjacentElement('beforebegin', panel);
  };

  const visiblePriceText = (node) => {
    if (!node) return '';
    const clone = node.cloneNode(true);
    clone.querySelectorAll('.sr-only').forEach((item) => item.remove());
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const installMobileStickyPrice = (section) => {
    const sticky = section.querySelector('product-sticky-bar.product-sticky-bar');
    if (!sticky) return;

    let price = sticky.querySelector('.ph-pdp-sticky-mobile-price');
    if (!price) {
      price = document.createElement('div');
      price.className = 'ph-pdp-sticky-mobile-price';
      price.setAttribute('aria-label', 'Current price');
      const buyButtons = sticky.querySelector('buy-buttons');
      if (buyButtons) buyButtons.insertAdjacentElement('beforebegin', price);
    }

    if (!price) return;

    const saleNode = sticky.querySelector('.product-sticky-bar__info sale-price');
    const compareNode = sticky.querySelector('.product-sticky-bar__info compare-at-price');
    const saleText = visiblePriceText(saleNode);
    const compareText = visiblePriceText(compareNode);
    if (!saleText) return;

    const nextKey = `${saleText}|${compareText}`;
    if (price.dataset.phStickyPriceKey === nextKey) return;
    price.dataset.phStickyPriceKey = nextKey;
    price.replaceChildren();

    if (compareText) {
      const label = document.createElement('span');
      label.className = 'ph-pdp-sticky-price-label';
      label.textContent = 'Sale price';
      price.append(label);
    }

    const sale = document.createElement('span');
    sale.className = 'ph-pdp-sticky-sale';
    sale.textContent = saleText;
    price.append(sale);

    if (compareText) {
      const compare = document.createElement('span');
      compare.className = 'ph-pdp-sticky-compare';
      compare.textContent = compareText;
      price.append(compare);
    }
  };

  const initialiseSection = (section) => {
    if (!section) return;
    section.classList.add('ph-pdp-version-b-active');
    section.querySelectorAll('product-gallery.product-gallery').forEach(initialiseGallery);
    installBenefits(section);
    installMobileStickyPrice(section);
  };

  const initialiseAll = () => {
    document.querySelectorAll('.shopify-section--main-product').forEach(initialiseSection);
  };

  const queueInitialise = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      initialiseAll();
    });
  };

  const start = () => {
    initialiseAll();

    const section = document.querySelector('.shopify-section--main-product');
    if (section) {
      sectionObserver = new MutationObserver(queueInitialise);
      sectionObserver.observe(section, { childList: true, subtree: true });
    }

    DESKTOP.addEventListener?.('change', () => {
      document.querySelectorAll('.shopify-section--main-product product-gallery.product-gallery').forEach(recalcGallery);
    });

    document.addEventListener('shopify:section:load', queueInitialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
